// =============================================================================
// schoolDiscovery types + CIP family labels (v0.2.32)
// =============================================================================

/** One school in the bundled discovery dataset (apps/web/public/data). */
export interface DiscoverySchool {
  id: number;
  name: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  url: string | null;
  netPriceCalcUrl: string | null;
  ownership: number | null;        // 1 public, 2 private NP, 3 private FP
  religious: number | null;        // religious affiliation code, or null
  locale: number | null;           // urbanicity code
  sizeSetting: number | null;      // Carnegie size/setting code
  region: number | null;
  hsi: number | null;              // Hispanic-Serving Institution flag (1/0)
  menOnly: number | null;
  womenOnly: number | null;
  mainCampus: number | null;
  onlineOnly: number | null;
  openAdmissions: number | null;
  accreditor: string | null;
  degreePredominant: number | null; // 0 none,1 cert,2 assoc,3 bach,4 grad
  degreeHighest: number | null;
  size: number | null;
  enrollmentAll: number | null;
  partTimeShare: number | null;
  studentFacultyRatio: number | null;
  pctWomen: number | null;
  pctMen: number | null;
  pctWhite: number | null;
  pctBlack: number | null;
  pctHispanic: number | null;
  pctAsian: number | null;
  pctAian: number | null;
  pctNhpi: number | null;
  pctTwoOrMore: number | null;
  pctNonResident: number | null;
  retentionRate: number | null;
  admitRate: number | null;
  testRequirements: number | null;
  satAvg: number | null;
  satReading25: number | null;
  satReading75: number | null;
  satMath25: number | null;
  satMath75: number | null;
  act25: number | null;
  act75: number | null;
  tuitionIn: number | null;
  tuitionOut: number | null;
  roomBoard: number | null;
  costOfAttendance: number | null;
  netPrice: number | null;
  gradRate: number | null;
  earnings10yr: number | null;
  majors: DiscoveryMajor[];
}

export interface DiscoveryMajor {
  code: string;   // 4-digit CIP, e.g. "5007"
  title: string;
  level: number | null;  // highest credential level offered (1..8)
}

export interface DiscoveryDataset {
  generated_at: string;
  source: string;
  school_count: number;
  schools: DiscoverySchool[];
}

export interface CipMajor {
  code: string;
  title: string;
}

// --- Reference maps ---------------------------------------------------------

/** degrees_awarded.predominant -> label */
export const SCHOOL_TYPE_LABELS: Record<number, string> = {
  0: 'Not classified',
  1: 'Trade / certificate',
  2: 'Community / 2-year',
  3: 'Four-year college',
  4: 'Graduate-only',
};

/** ownership -> label */
export const OWNERSHIP_LABELS: Record<number, string> = {
  1: 'Public',
  2: 'Private nonprofit',
  3: 'Private for-profit',
};

/** test_requirements -> label */
export const TEST_REQUIREMENT_LABELS: Record<number, string> = {
  1: 'Required',
  2: 'Recommended',
  3: 'Neither required nor recommended',
  4: 'Considered but not required',
  5: 'Considered if submitted',
};

/**
 * locale code -> simple setting bucket. College Scorecard locale codes:
 * 11-13 city, 21-23 suburb, 31-33 town, 41-43 rural.
 */
export function localeBucket(locale: number | null): 'City' | 'Suburb' | 'Town' | 'Rural' | null {
  if (locale == null) return null;
  if (locale >= 11 && locale <= 13) return 'City';
  if (locale >= 21 && locale <= 23) return 'Suburb';
  if (locale >= 31 && locale <= 33) return 'Town';
  if (locale >= 41 && locale <= 43) return 'Rural';
  return null;
}

/**
 * CIP 2-digit family labels. The 2-digit family is the first two characters
 * of a 4-digit CIP code. Labels are the standard federal CIP series titles.
 */
export const CIP_FAMILY_LABELS: Record<string, string> = {
  '01': 'Agriculture & Related Sciences',
  '03': 'Natural Resources & Conservation',
  '04': 'Architecture & Related Services',
  '05': 'Area, Ethnic, Cultural & Gender Studies',
  '09': 'Communication & Journalism',
  '10': 'Communications Technologies',
  '11': 'Computer & Information Sciences',
  '12': 'Personal & Culinary Services',
  '13': 'Education',
  '14': 'Engineering',
  '15': 'Engineering Technologies',
  '16': 'Foreign Languages & Linguistics',
  '19': 'Family & Consumer Sciences',
  '21': 'Reserved',
  '22': 'Legal Professions & Studies',
  '23': 'English Language & Literature',
  '24': 'Liberal Arts & Humanities',
  '25': 'Library Science',
  '26': 'Biological & Biomedical Sciences',
  '27': 'Mathematics & Statistics',
  '28': 'Military Science & ROTC',
  '29': 'Military Technologies',
  '30': 'Multi/Interdisciplinary Studies',
  '31': 'Parks, Recreation & Fitness',
  '32': 'Basic Skills & Developmental Education',
  '33': 'Citizenship Activities',
  '34': 'Health-Related Knowledge & Skills',
  '35': 'Interpersonal & Social Skills',
  '36': 'Leisure & Recreational Activities',
  '37': 'Personal Awareness & Self-Improvement',
  '38': 'Philosophy & Religious Studies',
  '39': 'Theology & Religious Vocations',
  '40': 'Physical Sciences',
  '41': 'Science Technologies',
  '42': 'Psychology',
  '43': 'Homeland Security, Law Enforcement & Firefighting',
  '44': 'Public Administration & Social Service',
  '45': 'Social Sciences',
  '46': 'Construction Trades',
  '47': 'Mechanic & Repair Technologies',
  '48': 'Precision Production',
  '49': 'Transportation & Materials Moving',
  '50': 'Visual & Performing Arts',
  '51': 'Health Professions',
  '52': 'Business, Management & Marketing',
  '53': 'High School / Secondary Diplomas',
  '54': 'History',
  '60': 'Dental, Medical & Veterinary Residency',
  '61': 'Medical Residency Programs',
};

/** The 2-digit family code for a 4-digit CIP code. */
export function cipFamily(code: string): string {
  return code.slice(0, 2);
}

/** Human label for a 2-digit family code (falls back to the code itself). */
export function cipFamilyLabel(family: string): string {
  return CIP_FAMILY_LABELS[family] ?? `CIP ${family}`;
}
